import React, { useEffect, useState } from "react";
import { Ticket, Newspaper, Calendar, ExternalLink, Sparkles } from "lucide-react";
import { api } from "../../lib/api";

/**
 * PromoCallout — standalone card that appears ABOVE the Walks Nearby
 * section on a campsite detail page when an active newspaper promo
 * matches at least one of the campsite's nearby operators
 * (e.g. Daily Mirror NT pass promo + a National Trust walk nearby).
 *
 * Silent no-op when there's nothing to show — so campsite pages away
 * from partner-owned walks are untouched.
 */
export default function PromoCallout({ campsiteId }) {
  const [promos, setPromos] = useState([]);

  useEffect(() => {
    if (!campsiteId) return;
    let alive = true;
    api.get(`/campsites/${campsiteId}/promos-nearby`)
      .then(({ data }) => { if (alive) setPromos(data.promos || []); })
      .catch(() => {});
    return () => { alive = false; };
  }, [campsiteId]);

  if (promos.length === 0) return null;

  return (
    <div className="mb-8 space-y-4" data-testid="promo-callout-section">
      {promos.map((p) => (
        <PromoCard key={p.promo_id} promo={p} />
      ))}
    </div>
  );
}

function PromoCard({ promo }) {
  const isLive = promo.is_active_now;
  const urgency = isLive
    ? `${promo.days_until_end} day${promo.days_until_end === 1 ? "" : "s"} left`
    : `Starts in ${promo.days_until_start} day${promo.days_until_start === 1 ? "" : "s"}`;

  return (
    <div
      className="border-2 border-[#0a7f3f] bg-white shadow-[0_4px_0_0_#0a7f3f] overflow-hidden"
      data-testid={`promo-card-${promo.promo_id}`}
    >
      <div className="bg-[#0a7f3f] text-white px-4 py-2 flex items-center gap-2 text-[11px] mono uppercase tracking-widest">
        <Sparkles className="w-3.5 h-3.5" />
        Free {promo.partner_display} passes — available nearby
        <span className="ml-auto bg-[#ff4500] px-2 py-0.5 font-black">{urgency}</span>
      </div>

      <div className="p-4 sm:p-5">
        <div className="flex items-start gap-3 mb-3">
          <div className="w-11 h-11 bg-[#0a7f3f]/10 text-[#0a7f3f] flex items-center justify-center shrink-0">
            <Ticket className="w-6 h-6" strokeWidth={1.75} />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-display text-lg sm:text-xl leading-tight" data-testid={`promo-title-${promo.promo_id}`}>
              {promo.title}
            </h3>
            {promo.subtitle && (
              <p className="text-sm text-[#444] mt-0.5">{promo.subtitle}</p>
            )}
          </div>
        </div>

        {promo.retailers?.length > 0 && (
          <div className="mb-3">
            <div className="text-[10px] mono uppercase tracking-widest text-[#666] mb-1.5 flex items-center gap-1.5">
              <Newspaper className="w-3 h-3" /> Grab it from
            </div>
            <div className="flex flex-wrap gap-2" data-testid={`promo-retailers-${promo.promo_id}`}>
              {promo.retailers.map((r, i) => (
                <RetailerChip key={i} retailer={r} />
              ))}
            </div>
          </div>
        )}

        <div className="border-t hairline pt-3 text-xs text-[#555] space-y-1">
          {promo.entitlement && (
            <div><span className="font-bold text-[#0e0e0e]">Includes:</span> {promo.entitlement}</div>
          )}
          <div className="flex items-center gap-1.5">
            <Calendar className="w-3.5 h-3.5 text-[#0a7f3f]" />
            <span>{promo.valid_from} → {promo.valid_to}</span>
          </div>
        </div>

        {promo.redemption_url && (
          <a
            href={promo.redemption_url}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-3 inline-flex items-center gap-1.5 bg-[#0a7f3f] text-white font-black text-[11px] uppercase tracking-widest px-4 py-2 shadow-[0_3px_0_0_#111] hover:translate-y-[1px] hover:shadow-[0_2px_0_0_#111] transition-transform"
            data-testid={`promo-cta-${promo.promo_id}`}
          >
            How to claim <ExternalLink className="w-3 h-3" />
          </a>
        )}
      </div>
    </div>
  );
}

function RetailerChip({ retailer }) {
  const [failed, setFailed] = useState(false);
  const logo = retailer.logo_domain
    ? `https://www.google.com/s2/favicons?domain=${retailer.logo_domain}&sz=64`
    : null;
  return (
    <span
      className="inline-flex items-center gap-1.5 border hairline pr-2.5 pl-1 py-1 text-[11px] font-semibold bg-[#fafafa]"
      title={retailer.purchase_hint || retailer.name}
    >
      {logo && !failed ? (
        <img
          src={logo}
          alt=""
          className="w-4 h-4 object-contain shrink-0"
          loading="lazy"
          referrerPolicy="no-referrer"
          onError={() => setFailed(true)}
        />
      ) : (
        <Newspaper className="w-3.5 h-3.5 text-[#666]" />
      )}
      {retailer.name}
      {retailer.purchase_hint && (
        <span className="text-[#777] font-normal">· {retailer.purchase_hint}</span>
      )}
    </span>
  );
}
